import React, { useState, useCallback, useEffect } from 'react';
import { useTournament } from '@shootout-comps/core';
import { useAuth } from '@shootout-comps/core';
import { useLang } from '@shootout-comps/i18n';
import MatchCard from '../components/MatchCard';
import LiveMatchCard from '../components/LiveMatchCard';
import ConnectionStatusBanner from '../components/ConnectionStatusBanner';
import SnookerBall from '../components/SnookerBall';
import type { Round } from '@shootout-comps/types';
import { SnookerBallSpinner } from '../components/SnookerBall';

interface BracketPageProps {
  tournamentId: string;
  onSelectMatch: (matchId: string) => void;
}

type BreakEntry = { playerId: string; playerName: string; points: number; matchId: string; breakIdx?: number };

const CARD_H  = 176; // fixed card height — accounts for LIVE badge + status row at maximum size
const CARD_GAP = 12;

// ── Top-10 breaks dialog ────────────────────────────────────────────────────
function TopBreaksDialog({ breaks, onClose }: { breaks: BreakEntry[]; onClose: () => void }) {
  const { t } = useLang();
  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 300,
        background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '1rem',
      }}
    >
      <div style={{
        background: 'linear-gradient(135deg, var(--baize), var(--felt))',
        border: '2px solid rgba(201,168,76,0.4)', borderRadius: '16px',
        padding: '1.75rem', width: '100%', maxWidth: '420px',
        boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.25rem' }}>
          <SnookerBall size={28} />
          <h2 style={{ fontFamily: 'Bebas Neue', fontSize: '1.8rem', color: 'var(--gold)', lineHeight: 1, flex: 1 }}>
            {t.highestBreakLabel}
          </h2>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', color: 'var(--chalk-dim)', fontSize: '1.4rem', cursor: 'pointer', lineHeight: 1, padding: '0.2rem' }}
          >
            ✕
          </button>
        </div>

        {/* Leaderboard */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
          {breaks.map((b, i) => {
            const medal = i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : null;
            const isTop = i === 0;
            return (
              <div key={`${b.matchId}-${b.playerId}-${b.breakIdx ?? 0}`} style={{
                display: 'flex', alignItems: 'center', gap: '0.75rem',
                background: isTop ? 'rgba(201,168,76,0.12)' : 'rgba(255,255,255,0.03)',
                border: `1px solid ${isTop ? 'rgba(201,168,76,0.35)' : 'rgba(255,255,255,0.07)'}`,
                borderRadius: '8px', padding: '0.6rem 0.9rem',
              }}>
                {/* Rank */}
                <div style={{ width: '28px', textAlign: 'center', flexShrink: 0 }}>
                  {medal
                    ? <span style={{ fontSize: '1.1rem' }}>{medal}</span>
                    : <span style={{ fontSize: '0.78rem', color: 'var(--chalk-dim)', fontWeight: 600 }}>#{i + 1}</span>
                  }
                </div>
                {/* Name */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontWeight: isTop ? 700 : 500,
                    color: isTop ? 'var(--chalk)' : 'var(--chalk-dim)',
                    fontSize: '0.9rem',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                    {b.playerName}
                  </div>
                </div>
                {/* Points */}
                <div style={{
                  fontFamily: 'Bebas Neue', fontSize: isTop ? '1.5rem' : '1.25rem',
                  color: isTop ? 'var(--gold)' : 'var(--chalk)',
                  letterSpacing: '0.04em', flexShrink: 0,
                }}>
                  {b.points}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Highest break pill (clickable) ──────────────────────────────────────────
function HighBreakPill({ topBreaks, size }: { topBreaks: BreakEntry[]; size: 'lg' | 'sm' }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const top = topBreaks[0];
  if (!top) return null;

  return (
    <>
      <div
        onClick={() => setOpen(true)}
        role="button"
        tabIndex={0}
        onKeyDown={e => e.key === 'Enter' && setOpen(true)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '0.6rem',
          background: 'rgba(201,168,76,0.07)',
          border: '1px solid rgba(201,168,76,0.2)',
          borderRadius: '10px',
          padding: size === 'lg' ? '0.6rem 1.2rem' : '0.5rem 1rem',
          cursor: 'pointer',
          transition: 'background 0.15s, border-color 0.15s',
        }}
        onMouseEnter={e => {
          (e.currentTarget as HTMLElement).style.background = 'rgba(201,168,76,0.14)';
          (e.currentTarget as HTMLElement).style.borderColor = 'rgba(201,168,76,0.4)';
        }}
        onMouseLeave={e => {
          (e.currentTarget as HTMLElement).style.background = 'rgba(201,168,76,0.07)';
          (e.currentTarget as HTMLElement).style.borderColor = 'rgba(201,168,76,0.2)';
        }}
      >
        <SnookerBall size={size === 'lg' ? 22 : 18} />
        <div style={{ textAlign: 'left' }}>
          <div style={{ fontSize: size === 'lg' ? '0.62rem' : '0.58rem', color: 'var(--chalk-dim)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            {t.highestBreakLabel}
          </div>
          <div style={{ fontWeight: 700, color: 'var(--gold)', fontSize: size === 'lg' ? '1rem' : '0.9rem' }}>
            {top.playerName} — {top.points} pts
          </div>
        </div>
        {topBreaks.length > 1 && (
          <div style={{ fontSize: '0.62rem', color: 'var(--chalk-dim)', marginLeft: '0.1rem' }}>
            +{topBreaks.length - 1} ›
          </div>
        )}
      </div>

      {open && <TopBreaksDialog breaks={topBreaks} onClose={() => setOpen(false)} />}
    </>
  );
}

export default function BracketPage({ tournamentId, onSelectMatch }: BracketPageProps) {
  const { tournament, fetchTournament } = useTournament();
  const { getAccessToken } = useAuth();
  const { t } = useLang();

  useEffect(() => { fetchTournament(tournamentId); }, [tournamentId]); // eslint-disable-line
  const [editMatch, setEditMatch] = useState<{ matchId: string; p1: import('@shootout-comps/types').Player; p2: import('@shootout-comps/types').Player; score1: number; score2: number; winnerId: string } | null>(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');

  const handleEditOpen = useCallback((matchId: string) => {
    if (!tournament) return;
    for (const round of tournament.rounds) {
      const m = round.matches.find(x => x._id === matchId);
      if (m && m.player2) {
        setEditMatch({
          matchId,
          p1: m.player1 as import('@shootout-comps/types').Player,
          p2: m.player2 as import('@shootout-comps/types').Player,
          score1: m.score1 ?? 0,
          score2: m.score2 ?? 0,
          winnerId: m.winner?._id ?? String((m.player1 as import('@shootout-comps/types').Player)._id),
        });
        setEditError('');
        return;
      }
    }
  }, [tournament]);

  const handleEditSave = useCallback(async (matchId: string, score1: number, score2: number, winnerId: string) => {
    setEditSaving(true); setEditError('');
    try {
      const token = await getAccessToken();
      const res = await fetch(`/api/matches/${matchId}/result`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ score1, score2, winnerId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? 'Failed to save');
      setEditMatch(null);
      // tournament:updated socket event will refresh the bracket automatically
    } catch (e) {
      setEditError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setEditSaving(false);
    }
  }, [getAccessToken]);

  // Guard against briefly rendering a previously-open tournament's stale data while the
  // fetch for this one is still in flight.
  if (!tournament || tournament._id !== tournamentId) return null;

  const tAny = tournament as unknown as {
    topBreaks?: BreakEntry[];
    highestBreak?: BreakEntry | null;
    format?: 'shootout' | 'snooker900' | 'standard';
    sixReds?: boolean;
    entryFee?: number | null;
    currency?: string;
    prizes?: { place: number; amount: number }[];
    highBreakPrize?: number | null;
    maxPlayers?: number;
    isHandicap?: boolean;
    location?: string | null;
    scheduledAt?: string | null;
  };

  // Use topBreaks if available, fall back to single highestBreak for old data
  const topBreaks: BreakEntry[] = tAny.topBreaks && tAny.topBreaks.length > 0
    ? tAny.topBreaks
    : tAny.highestBreak ? [tAny.highestBreak] : [];

  const sym = (tAny.currency && ['GBP','EUR','USD','AUD','CAD','CHF','SEK','NOK','DKK','INR','JPY','CNY','ZAR'].includes(tAny.currency))
    ? ({ GBP:'£',EUR:'€',USD:'$',AUD:'A$',CAD:'C$',CHF:'Fr',SEK:'kr',NOK:'kr',DKK:'kr',INR:'₹',JPY:'¥',CNY:'¥',ZAR:'R' } as Record<string,string>)[tAny.currency]
    : tAny.currency ?? '£';

  const formatBadge = tAny.format === 'snooker900'
    ? { label: t.bracketSnooker900Label, icon: '🕐', color: '#3498db', bg: 'rgba(52,152,219,0.14)', border: 'rgba(52,152,219,0.35)' }
    : tAny.format === 'standard'
    ? { label: t.bracketStandardLabel, icon: '🔴', color: '#9b59b6', bg: 'rgba(155,89,182,0.14)', border: 'rgba(155,89,182,0.35)' }
    : { label: t.bracketShootoutLabel, icon: '⚡', color: '#e67e22', bg: 'rgba(230,126,34,0.14)', border: 'rgba(230,126,34,0.35)' };

  const prizeTotal = tAny.prizes?.reduce((s, p) => s + p.amount, 0) ?? 0;
  const realPlayers = tournament.players?.filter((p: { isBye?: boolean }) => !p.isBye).length ?? 0;

  /** Compact info strip shown at the top of the bracket */
  const TournamentInfoBanner = () => (
    <div style={{
      background: 'linear-gradient(135deg, rgba(201,168,76,0.07), rgba(201,168,76,0.02))',
      borderBottom: '1px solid rgba(201,168,76,0.18)',
      padding: '0.65rem 1rem',
    }}>
      {/* Name + badges row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
        <span style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--chalk)', marginRight: '0.2rem' }}>{tournament.name}</span>
        {/* Format badge */}
        <span style={{ fontSize: '0.68rem', fontWeight: 700, background: formatBadge.bg, color: formatBadge.color, border: `1px solid ${formatBadge.border}`, borderRadius: '5px', padding: '0.12rem 0.45rem', letterSpacing: '0.03em', whiteSpace: 'nowrap' }}>
          {formatBadge.icon} {formatBadge.label}
        </span>
        {tAny.sixReds && (
          <span style={{ fontSize: '0.68rem', fontWeight: 700, background: 'rgba(192,57,43,0.12)', color: '#e74c3c', border: '1px solid rgba(192,57,43,0.3)', borderRadius: '5px', padding: '0.12rem 0.45rem', whiteSpace: 'nowrap' }}>
            🔴 {t.bracketSixReds}
          </span>
        )}
        {tAny.isHandicap && (
          <span style={{ fontSize: '0.68rem', fontWeight: 700, background: 'rgba(46,204,113,0.1)', color: '#2ecc71', border: '1px solid rgba(46,204,113,0.3)', borderRadius: '5px', padding: '0.12rem 0.45rem', whiteSpace: 'nowrap' }}>
            🏅 {t.bracketHandicap}
          </span>
        )}
      </div>
      {/* Stats row */}
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', fontSize: '0.76rem', color: 'var(--chalk-dim)', alignItems: 'center' }}>
        <span>👥 <strong style={{ color: 'var(--chalk)' }}>{realPlayers}</strong>{tAny.maxPlayers ? `/${tAny.maxPlayers}` : ''} {realPlayers === 1 ? t.bracketEntry : t.bracketEntries}</span>
        {tAny.entryFee != null && tAny.entryFee > 0 && (
          <span>💰 <strong style={{ color: 'var(--chalk)' }}>{sym}{tAny.entryFee}</strong> {t.bracketEntry}</span>
        )}
        {prizeTotal > 0 && (
          <span>🏆 <strong style={{ color: 'var(--gold)' }}>{sym}{prizeTotal.toFixed(2)}</strong> {t.bracketPrizePool} · {(tAny.prizes?.length ?? 0)} {(tAny.prizes?.length ?? 0) !== 1 ? t.bracketPlaces : t.bracketPlace}</span>
        )}
        {tAny.highBreakPrize != null && tAny.highBreakPrize > 0 && (
          <span>🎯 <strong style={{ color: 'var(--gold)' }}>{sym}{(tAny.highBreakPrize as number).toFixed(2)}</strong> {t.bracketHighBreak}</span>
        )}
        {tAny.scheduledAt && (
          <span>📅 <strong style={{ color: 'var(--chalk)' }}>{new Date(tAny.scheduledAt).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}</strong></span>
        )}
        {tAny.location && (
          <span>📍 <strong style={{ color: 'var(--chalk)' }}>{tAny.location}</strong></span>
        )}
      </div>
    </div>
  );

  // A round's matches are editable when the NEXT round has no played matches
  const editableRoundIndices = new Set<number>(
    tournament.rounds.map((_, idx) => {
      const next = tournament.rounds[idx + 1];
      if (!next) return idx; // last round always editable
      const nextPlayed = next.matches.some(m => m.status === 'active' || m.status === 'complete');
      return nextPlayed ? -1 : idx;
    }).filter(i => i >= 0)
  );

  if (tournament.status === 'registration') {
    return (
      <div>
        <ConnectionStatusBanner />
        <TournamentInfoBanner />
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', minHeight: 'calc(100vh - 130px)',
          gap: '1rem', color: 'var(--chalk-dim)',
        }}>
          <SnookerBallSpinner size={64} />
          <h2 style={{ fontFamily: 'Bebas Neue', fontSize: '2rem', color: 'var(--gold)' }}>{t.awaitingDraw}</h2>
          <p style={{ fontSize: '0.9rem' }}>{t.awaitingDrawDesc}</p>
        </div>
      </div>
    );
  }

  if (tournament.status === 'complete') {
    const finalRound = tournament.rounds[tournament.rounds.length - 1];
    const champion = finalRound?.matches[0]?.winner;
    return (
      <div>
        <ConnectionStatusBanner />
        <TournamentInfoBanner />
        {champion && (
          <div style={{
            textAlign: 'center', padding: '3rem 1rem 2rem',
            background: 'linear-gradient(180deg, rgba(201,168,76,0.1) 0%, transparent 100%)',
            borderBottom: '1px solid rgba(201,168,76,0.2)', marginBottom: '2rem',
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>🏆</div>
            <h2 style={{ fontFamily: 'Bebas Neue', fontSize: '3rem', color: 'var(--gold)', lineHeight: 1 }}>{t.bracketChampionTitle}</h2>
            <p style={{ fontSize: '1.8rem', fontWeight: 600, marginTop: '0.5rem' }}>{champion.name}</p>
            {topBreaks.length > 0 && (
              <div style={{ marginTop: '1.25rem' }}>
                <HighBreakPill topBreaks={topBreaks} size="lg" />
              </div>
            )}
          </div>
        )}
        <BracketRounds rounds={tournament.rounds} onSelectMatch={onSelectMatch} editableRoundIndices={editableRoundIndices} onEditMatch={handleEditOpen} tournamentId={tournamentId} isHandicap={tAny.isHandicap} sixReds={tAny.sixReds} />
      </div>
    );
  }

  return (
    <div>
      <ConnectionStatusBanner />
      <TournamentInfoBanner />
      {editMatch && (
        <EditResultDialog
          matchId={editMatch.matchId}
          p1={editMatch.p1}
          p2={editMatch.p2}
          initialScore1={editMatch.score1}
          initialScore2={editMatch.score2}
          initialWinnerId={editMatch.winnerId}
          saving={editSaving}
          error={editError}
          onSave={handleEditSave}
          onClose={() => setEditMatch(null)}
        />
      )}
      {topBreaks.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '0.75rem 1rem 0' }}>
          <HighBreakPill topBreaks={topBreaks} size="sm" />
        </div>
      )}
      <BracketRounds
        rounds={tournament.rounds}
        onSelectMatch={onSelectMatch}
        editableRoundIndices={editableRoundIndices}
        onEditMatch={handleEditOpen}
        tournamentId={tournamentId}
        isHandicap={tAny.isHandicap}
        sixReds={tAny.sixReds}
      />
    </div>
  );
}

function getRoundName(totalRounds: number, roundIdx: number, t: { roundFinal: string; roundSemiFinals: string; roundQuarterFinals: string; roundOf16: string; roundOf: string }): string {
  const fromEnd = totalRounds - 1 - roundIdx;
  if (fromEnd === 0) return t.roundFinal;
  if (fromEnd === 1) return t.roundSemiFinals;
  if (fromEnd === 2) return t.roundQuarterFinals;
  if (fromEnd === 3) return t.roundOf16;
  return `${t.roundOf} ${Math.pow(2, fromEnd + 1)}`;
}

function BracketRounds({ rounds, onSelectMatch, editableRoundIndices, onEditMatch, tournamentId, isHandicap, sixReds }: {
  rounds: Round[];
  onSelectMatch: (id: string) => void;
  editableRoundIndices: Set<number>;
  onEditMatch: (matchId: string) => void;
  tournamentId: string;
  isHandicap?: boolean;
  sixReds?: boolean;
}) {



  const r1Matches = rounds[0]?.matches.length ?? 1;
  const totalRounds = Math.log2(r1Matches) + 1;

  return (
    // marginRight (not flex `gap`) spaces the round columns — `gap` on flex containers isn't
    // supported by the older embedded browser engines some smart TVs ship with. The trailing
    // margin on the last column is harmless (just unused whitespace at the end of the scroll area).
    <div style={{ display: 'flex', padding: '2rem 1.5rem', overflowX: 'auto', overflowY: 'auto' }}>
      {rounds.map((round, roundIdx) => (
        <div key={round.round} style={{ marginRight: '1.5rem', flexShrink: 0 }}>
          <RoundColumn
            round={round}
            roundIdx={roundIdx}
            totalRounds={totalRounds}
            onSelectMatch={onSelectMatch}
            editable={editableRoundIndices.has(roundIdx)}
            onEditMatch={onEditMatch}
            tournamentId={tournamentId}
            isHandicap={isHandicap}
            sixReds={sixReds}
          />
        </div>
      ))}
    </div>
  );
}

function RoundColumn({
  round, roundIdx, totalRounds, onSelectMatch, editable, onEditMatch, tournamentId, isHandicap, sixReds,
}: {
  round: Round;
  roundIdx: number;
  totalRounds: number;
  onSelectMatch: (id: string) => void;
  editable: boolean;
  onEditMatch: (matchId: string) => void;
  tournamentId: string;
  isHandicap?: boolean;
  sixReds?: boolean;
}) {
  const { t } = useLang();
  const done = round.matches.every((m) => m.status === 'complete');
  const roundName = getRoundName(totalRounds, roundIdx, t);

  const h = CARD_H;
  const unitSpacing = h + CARD_GAP;
  const centreSpacing = unitSpacing * Math.pow(2, roundIdx);
  const firstTop = centreSpacing / 2 - h / 2;
  const cardsH = firstTop + round.matches.length * h + (round.matches.length - 1) * (centreSpacing - h);

  return (
    <div style={{ minWidth: '230px', maxWidth: '250px', flexShrink: 0 }}>
      <div style={{
        textAlign: 'center', marginBottom: '1rem', paddingBottom: '0.75rem',
        borderBottom: `2px solid ${done ? 'var(--gold)' : 'rgba(255,255,255,0.1)'}`,
      }}>
        <h3 style={{ fontFamily: 'Bebas Neue', fontSize: '1.4rem', color: done ? 'var(--gold)' : 'var(--chalk)', letterSpacing: '0.04em' }}>
          {roundName}
        </h3>
        <span style={{ fontSize: '0.7rem', color: 'var(--chalk-dim)' }}>
          {round.matches.filter((m) => m.status === 'complete').length} / {round.matches.length} {t.matchesPlayed}
        </span>
      </div>

      <div style={{ position: 'relative', height: cardsH }}>
        {round.matches.map((match, i) => (
          <div
            key={match._id}
            style={{ position: 'absolute', top: firstTop + i * centreSpacing, left: 0, right: 0 }}
          >
            {match.status === 'active'
              ? <LiveMatchCard match={match} tournamentId={tournamentId} onSelect={onSelectMatch} isHandicap={isHandicap} sixReds={sixReds} onEdit={editable ? onEditMatch : undefined} />
              : <MatchCard match={match} onSelect={onSelectMatch} isHandicap={isHandicap} sixReds={sixReds} onEdit={editable ? onEditMatch : undefined} />}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Edit Result Dialog ────────────────────────────────────────────────────────
function EditResultDialog({
  matchId, p1, p2, initialScore1, initialScore2, initialWinnerId, saving, error, onSave, onClose,
}: {
  matchId: string;
  p1: import('@shootout-comps/types').Player;
  p2: import('@shootout-comps/types').Player;
  initialScore1: number;
  initialScore2: number;
  initialWinnerId: string;
  saving: boolean;
  error: string;
  onSave: (matchId: string, score1: number, score2: number, winnerId: string) => void;
  onClose: () => void;
}) {
  const [score1, setScore1] = React.useState(String(initialScore1));
  const [score2, setScore2] = React.useState(String(initialScore2));
  const [winnerId, setWinnerId] = React.useState(initialWinnerId);
  const { t } = useLang();

  const s1 = parseInt(score1, 10);
  const s2 = parseInt(score2, 10);
  const valid = !isNaN(s1) && !isNaN(s2) && s1 >= 0 && s2 >= 0 && (winnerId === String(p1._id) || winnerId === String(p2._id));

  const inputStyle: React.CSSProperties = {
    background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: '8px', padding: '0.55rem 0.75rem', color: 'var(--chalk)',
    fontSize: '1.4rem', fontFamily: 'Bebas Neue', width: '80px', textAlign: 'center',
    outline: 'none',
  };

  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
    >
      <div style={{ background: 'linear-gradient(135deg, var(--baize), var(--felt))', border: '2px solid rgba(201,168,76,0.4)', borderRadius: '16px', padding: '1.75rem', width: '100%', maxWidth: '400px', boxShadow: '0 24px 64px rgba(0,0,0,0.6)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
          <h2 style={{ fontFamily: 'Bebas Neue', fontSize: '1.6rem', color: 'var(--gold)', lineHeight: 1, margin: 0 }}>
            ✏ Edit Result
          </h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--chalk-dim)', fontSize: '1.3rem', cursor: 'pointer' }}>✕</button>
        </div>

        {/* Score row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', justifyContent: 'center', marginBottom: '1.25rem' }}>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontSize: '0.72rem', color: 'var(--chalk-dim)', marginBottom: '0.4rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p1.name}</div>
            <input type="number" min={0} value={score1} onChange={e => setScore1(e.target.value)} style={inputStyle} />
          </div>
          <div style={{ fontFamily: 'Bebas Neue', fontSize: '1.4rem', color: 'var(--chalk-dim)', flexShrink: 0 }}>—</div>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontSize: '0.72rem', color: 'var(--chalk-dim)', marginBottom: '0.4rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p2.name}</div>
            <input type="number" min={0} value={score2} onChange={e => setScore2(e.target.value)} style={inputStyle} />
          </div>
        </div>

        {/* Winner picker */}
        <div style={{ marginBottom: '1.25rem' }}>
          <div style={{ fontSize: '0.65rem', color: 'var(--chalk-dim)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '0.5rem' }}>{t.bracketWinner}</div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {[p1, p2].map(p => {
              const selected = winnerId === String(p._id);
              return (
                <button key={String(p._id)} onClick={() => setWinnerId(String(p._id))} style={{ flex: 1, padding: '0.6rem 0.5rem', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem', background: selected ? 'rgba(201,168,76,0.18)' : 'rgba(255,255,255,0.05)', color: selected ? 'var(--gold)' : 'var(--chalk-dim)', border: `1.5px solid ${selected ? 'var(--gold)' : 'rgba(255,255,255,0.12)'}`, transition: 'all 0.15s', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {selected ? '🏆 ' : ''}{p.name}
                </button>
              );
            })}
          </div>
        </div>

        {error && (
          <div style={{ background: 'rgba(192,57,43,0.12)', border: '1px solid rgba(192,57,43,0.3)', borderRadius: '8px', padding: '0.6rem 0.75rem', color: '#e74c3c', fontSize: '0.82rem', marginBottom: '1rem' }}>
            {error}
          </div>
        )}

        <button
          onClick={() => valid && onSave(matchId, s1, s2, winnerId)}
          disabled={!valid || saving}
          style={{ width: '100%', padding: '0.85rem', background: valid ? 'linear-gradient(135deg, var(--gold), var(--gold-light))' : 'rgba(255,255,255,0.07)', color: valid ? 'var(--baize-dark)' : 'var(--chalk-dim)', border: 'none', borderRadius: '10px', fontSize: '0.95rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', cursor: valid && !saving ? 'pointer' : 'not-allowed', opacity: saving ? 0.7 : 1 }}
        >
          {saving ? t.bracketSavingLabel : t.bracketSaveResult}
        </button>
      </div>
    </div>
  );
}
